import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { LogIn, LogOut, UserRound } from "lucide-react";
import { AuthModal } from "./components/AuthModal";
import { LaunchNoticeModal } from "./components/LaunchNoticeModal";
import { GalaxyBackground } from "./components/GalaxyBackground";
import { defaultSiteContent } from "./content/defaultSiteContent";
import { AccountPage, CheckoutPage, PaymentResultPage, PricingPage } from "./pages/BillingPages";
import { CanvasPage } from "./pages/CanvasPage";
import { HomePage } from "./pages/HomePage";
import { api, DownloadRateLimitError, type AppSettings, type AuthSessionState, type SiteStats, type User } from "./services/api";
import { type Language } from "./types/siteContent";

// App.tsx is the frontend coordinator.
// It decides which page to show, keeps shared state, calls the backend through api.ts,
// and passes data plus event handlers down into page components.
const defaultAppSettings: AppSettings = {
  downloadMode: "production",
  paymentMode: "test"
};

// Homepage stats are refreshed on an interval. Keeping the value here makes it easy to tune.
const STATS_POLL_INTERVAL_MS = 5000;
const AUTH_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const CANVAS_SESSION_STORAGE_KEY = "maipo-canvas-session-user";
const AdminPage = lazy(() => import("./pages/AdminPage").then((module) => ({ default: module.AdminPage })));

function userDisplayName(user: User) {
  // Header display name priority: nickname first, then email, then phone.
  return user.nickname || user.email || user.phone || "Account";
}

function isElementInViewport(element: HTMLElement) {
  // Used by reveal animations to decide whether an element is already visible.
  const rect = element.getBoundingClientRect();
  return rect.top < window.innerHeight * 0.92 && rect.bottom > 0;
}

async function fetchAppSettings() {
  // Runtime settings come from the API so admins can change modes without rebuilding.
  // If the API is unavailable, the page falls back to safe defaults instead of crashing.
  try {
    const { settings } = await api.appSettings();
    return settings;
  } catch {
    return defaultAppSettings;
  }
}

export default function App() {
  // This app uses a lightweight router based on window.location instead of react-router.
  // The project has only a few routes, so keeping routing here makes the flow easier to trace.
  const isAdminRoute = window.location.pathname.startsWith("/admin");
  const initialAuthMode = new URLSearchParams(window.location.search).get("auth");

  // path/searchText mirror the browser address bar. They drive page selection below.
  const [path, setPath] = useState(window.location.pathname);
  const [searchText, setSearchText] = useState(window.location.search);

  // Language is stored locally so returning visitors keep their preference.
  const [language, setLanguage] = useState<Language>(() => {
    return (localStorage.getItem("maipo-lang") as Language) || "zh";
  });

  // The following state is shared across multiple pages.
  // user is a normal public/download account. Admin identity is handled inside AdminPage.
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authOpen, setAuthOpen] = useState(initialAuthMode === "login" || initialAuthMode === "register");
  const [launchNoticeOpen, setLaunchNoticeOpen] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState<string | null>(null);
  const [downloadPending, setDownloadPending] = useState(false);
  const [appSettings, setAppSettings] = useState<AppSettings>(defaultAppSettings);
  const [postAuthPath, setPostAuthPath] = useState<string | null>(null);

  // Homepage content starts from bundled fallback data, then gets replaced by published CMS content.
  const [siteContent, setSiteContent] = useState(defaultSiteContent);

  // Public stats are optional. null means loading failed or has not completed yet.
  const [siteStats, setSiteStats] = useState<SiteStats | null>(null);
  const [authSession, setAuthSession] = useState<AuthSessionState>({ rememberMe: false });
  const [canvasAuthChecked, setCanvasAuthChecked] = useState(false);
  const [canvasAuthenticated, setCanvasAuthenticated] = useState(false);

  useEffect(() => {
    // Keep React state in sync when the user clicks browser Back or Forward.
    function syncPath() {
      setPath(window.location.pathname);
      setSearchText(window.location.search);
    }

    window.addEventListener("popstate", syncPath);
    return () => window.removeEventListener("popstate", syncPath);
  }, []);

  useEffect(() => {
    // Update document language and remember the language choice.
    document.documentElement.lang = language === "en" ? "en" : "zh-CN";
    localStorage.setItem("maipo-lang", language);
  }, [language]);

  useEffect(() => {
    // The public site is locked to the dark theme while the theme switch is removed.
    document.body.dataset.theme = "dark";
    localStorage.setItem("maipo-theme", "dark");
  }, []);

  useEffect(() => {
    // Public pages need three startup requests:
    // 1. Check whether the visitor is logged in.
    // 2. Load published CMS content for the homepage.
    // 3. Load runtime settings such as download and payment mode.
    //
    // /admin is skipped because the admin page has its own auth and API flow.
    if (isAdminRoute) return;
    setAuthChecked(false);
    api.me().then(({ user, rememberMe }) => {
      setUser(user);
      setAuthSession({ rememberMe });
    }).catch(() => {
      setUser(null);
      setAuthSession({ rememberMe: false });
    }).finally(() => setAuthChecked(true));
    api.siteContent().then(({ content }) => setSiteContent(content)).catch(() => setSiteContent(defaultSiteContent));
    fetchAppSettings().then(setAppSettings);
  }, [isAdminRoute]);

  useEffect(() => {
    if (isAdminRoute || path !== "/canvas" || !authChecked) return;

    if (!user) {
      setCanvasAuthenticated(false);
      setCanvasAuthChecked(true);
      return;
    }

    const storedUserId = sessionStorage.getItem(CANVAS_SESSION_STORAGE_KEY);
    if (storedUserId !== user.id) {
      setCanvasAuthenticated(false);
      setCanvasAuthChecked(true);
      return;
    }

    let cancelled = false;
    setCanvasAuthChecked(false);
    api.canvasSession().then(({ user: canvasUser }) => {
      if (cancelled) return;
      const valid = canvasUser.id === user.id;
      setCanvasAuthenticated(valid);
      if (!valid) sessionStorage.removeItem(CANVAS_SESSION_STORAGE_KEY);
    }).catch(() => {
      if (cancelled) return;
      sessionStorage.removeItem(CANVAS_SESSION_STORAGE_KEY);
      setCanvasAuthenticated(false);
    }).finally(() => {
      if (!cancelled) setCanvasAuthChecked(true);
    });

    return () => {
      cancelled = true;
    };
  }, [authChecked, isAdminRoute, path, user?.id]);

  useEffect(() => {
    if (isAdminRoute) return;

    function refreshStats() {
      // Stats are display-only. A stats request failure should not block the homepage.
      api.stats().then(({ stats }) => setSiteStats(stats)).catch(() => undefined);
    }

    // Fetch immediately, then poll. The server excludes /api/stats from request counting.
    refreshStats();
    const timer = window.setInterval(refreshStats, STATS_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isAdminRoute]);

  useEffect(() => {
    if (isAdminRoute || !user || !authSession.rememberMe) return;
    let cancelled = false;
    let lastRefreshAt = Date.now();

    async function refreshAuthSession() {
      try {
        const { user: refreshedUser, rememberMe } = await api.refresh();
        if (cancelled) return;
        setUser(refreshedUser);
        setAuthSession({ rememberMe });
        lastRefreshAt = Date.now();
      } catch (error) {
        if (cancelled) return;
        if (error instanceof Error && (error.message === "Login required." || error.message === "Session revoked.")) {
          setUser(null);
          setAuthSession({ rememberMe: false });
        }
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible" && Date.now() - lastRefreshAt >= AUTH_REFRESH_INTERVAL_MS) {
        void refreshAuthSession();
      }
    }

    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void refreshAuthSession();
      }
    }, AUTH_REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [authSession.rememberMe, isAdminRoute, user]);

  useEffect(() => {
    // ?auth=login/register opens the modal once, then the URL is cleaned up.
    if (isAdminRoute || !initialAuthMode) return;
    const cleanUrl = `${window.location.pathname}${window.location.hash}`;
    window.history.replaceState({}, "", cleanUrl);
    setSearchText(window.location.search);
  }, [initialAuthMode, isAdminRoute]);

  useEffect(() => {
    // Apply scroll reveal animations to sections that use the .reveal class.
    // The effect reruns when content, language, or path changes because those can change rendered sections.
    if (isAdminRoute) return;
    const elements = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.18,
      rootMargin: "0px 0px -8% 0px"
    });

    elements.forEach((element) => {
      if (element.classList.contains("is-visible") || isElementInViewport(element)) {
        element.classList.add("is-visible");
        return;
      }

      observer.observe(element);
    });
    return () => observer.disconnect();
  }, [isAdminRoute, language, path, siteContent]);

  const authLabel = useMemo(() => {
    // Header auth button text: show the user name when logged in, otherwise show sign-in text.
    if (user) return userDisplayName(user);
    return language === "en" ? "Sign in" : "登录";
  }, [language, user]);

  async function handleDownload() {
    // Download button flow:
    // 1. If not logged in, show a message and open the auth modal.
    // 2. If logged in, re-read runtime settings in case an admin changed them.
    // 3. In development mode, show the launch notice instead of downloading.
    // 4. In production mode, preflight the installer endpoint, then let the
    //    browser perform the binary download natively so progress starts at once.
    if (downloadPending) return;
    setDownloadPending(true);
    setDownloadMessage(null);

    try {
      if (!user) {
        setDownloadMessage(language === "en" ? "Please sign in before downloading." : "\u8bf7\u5148\u767b\u5f55\u540e\u518d\u4e0b\u8f7d\u5b89\u88c5\u5305\u3002");
        setAuthOpen(true);
        return;
      }

      const latestSettings = await fetchAppSettings();
      setAppSettings(latestSettings);

      if (latestSettings.downloadMode === "development") {
        setLaunchNoticeOpen(true);
        return;
      }

      await api.downloadLatest();
      // Native browser downloads happen outside the fetch lifecycle. Delay the
      // stats refresh briefly so the server has time to create DownloadLog.
      window.setTimeout(() => {
        api.stats().then(({ stats }) => setSiteStats(stats)).catch(() => undefined);
      }, 1500);
    } catch (error) {
      if (error instanceof DownloadRateLimitError) {
        const waitText = error.retryAfterSeconds
          ? (language === "en" ? ` Please try again in ${error.retryAfterSeconds} seconds.` : `\u8bf7 ${error.retryAfterSeconds} \u79d2\u540e\u518d\u8bd5\u3002`)
          : "";
        setDownloadMessage((language === "en" ? "Too many download attempts." : "\u4e0b\u8f7d\u8fc7\u4e8e\u9891\u7e41\u3002") + waitText);
        return;
      }
      setDownloadMessage((error as Error).message || (language === "en" ? "Download failed." : "\u4e0b\u8f7d\u5931\u8d25\u3002"));
    } finally {
      window.setTimeout(() => setDownloadPending(false), 2500);
    }
  }
  async function handleLogout() {
    // Logout clears the server session, then clears local user state.
    // The local state is cleared even if the network request fails.
    await api.logout().catch(() => undefined);
    sessionStorage.removeItem(CANVAS_SESSION_STORAGE_KEY);
    setUser(null);
    setAuthSession({ rememberMe: false });
    setCanvasAuthenticated(false);
    setCanvasAuthChecked(false);
  }

  async function handleCanvasLogout() {
    await api.logout().catch(() => undefined);
    sessionStorage.removeItem(CANVAS_SESSION_STORAGE_KEY);
    setUser(null);
    setAuthSession({ rememberMe: false });
    setCanvasAuthenticated(false);
    setCanvasAuthChecked(true);
    setPostAuthPath("/canvas");
    setAuthOpen(true);
  }

  function navigate(nextPath: string) {
    // Internal navigation changes the URL and React state without a full browser reload.
    window.history.pushState({}, "", nextPath);
    setPath(window.location.pathname);
    setSearchText(window.location.search);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleCanvasOpen() {
    if (user) {
      navigate("/canvas");
      return;
    }
    setPostAuthPath("/canvas");
    setAuthOpen(true);
  }

  const search = new URLSearchParams(searchText);
  const accountPreview = path === "/account" && search.get("preview") === "1";

  useEffect(() => {
    // Direct visits to /account require login unless preview=1 is present.
    if (isAdminRoute || !authChecked) return;

    if (path === "/account" && !user && !accountPreview) {
      setAuthOpen(true);
      return;
    }

    if (
      path === "/canvas"
      && (!user || (canvasAuthChecked && !canvasAuthenticated))
    ) {
      setPostAuthPath("/canvas");
      setAuthOpen(true);
    }
  }, [accountPreview, authChecked, canvasAuthChecked, canvasAuthenticated, isAdminRoute, path, user]);

  useEffect(() => {
    if (isAdminRoute || !path.startsWith("/mock-pay/")) return;
    setLaunchNoticeOpen(true);
  }, [isAdminRoute, path]);

  if (isAdminRoute) {
    // /admin is fully owned by AdminPage and uses separate admin auth.
    return (
      <Suspense fallback={<main className="admin-shell admin-login-shell"><p className="admin-empty">后台加载中...</p></main>}>
        <AdminPage />
      </Suspense>
    );
  }

  // Billing-related pages share these props, so they are grouped once here.
  const billingProps = {
    user,
    isPreview: accountPreview,
    appSettings,
    purchaseEnabled: true,
    onAuthOpen: () => setAuthOpen(true),
    onLaunchNotice: () => setLaunchNoticeOpen(true),
    onLogout: handleLogout,
    onUserUpdate: setUser,
    navigate
  };

  // Default page is the public homepage. The path checks below can replace it.
  let page = (
    <HomePage
      language={language}
      user={user}
      authLabel={authLabel}
      downloadMessage={downloadMessage}
      downloadPending={downloadPending}
      content={siteContent}
      stats={siteStats}
      onLanguageChange={setLanguage}
      onAuthOpen={() => setAuthOpen(true)}
      onAccountOpen={() => navigate("/account")}
      onPricingOpen={() => setLaunchNoticeOpen(true)}
      onCanvasOpen={handleCanvasOpen}
      onLogout={handleLogout}
      onDownload={handleDownload}
      authIcon={user ? <UserRound size={15} /> : <LogIn size={15} />}
      logoutIcon={<LogOut size={15} />}
    />
  );

  if (path === "/canvas") {
    page = (
      <CanvasPage
        language={language}
        isReady={authChecked && (!user || canvasAuthChecked)}
        isAuthenticated={Boolean(user)}
        isCanvasAuthenticated={canvasAuthenticated}
        user={user}
        onAuthOpen={() => {
          setPostAuthPath("/canvas");
          setAuthOpen(true);
        }}
        onLogout={handleCanvasLogout}
        onBackHome={() => navigate("/")}
      />
    );
  } else if (path === "/account") {
    // Account center: requires login, except for /account?preview=1.
    page = (authChecked && user) || accountPreview
      ? <AccountPage {...billingProps} />
      : (
        <main className="billing-shell">
          <section className="billing-card billing-empty-state">
            <UserRound size={28} />
            <h2>登录后查看个人中心</h2>
            <p>请先登录，登录成功后会自动显示个人中心。</p>
          </section>
        </main>
      );
  } else if (path === "/pricing") {
    // Pricing page.
    page = <PricingPage {...billingProps} />;
  } else if (path.startsWith("/checkout/")) {
    // Checkout page. /checkout/:planId contains the selected membership plan id.
    page = <CheckoutPage {...billingProps} planId={decodeURIComponent(path.replace("/checkout/", ""))} orderNo={search.get("order")} />;
  } else if (path.startsWith("/mock-pay/")) {
    // Public test payment is no longer exposed. Keep deep links inside the app shell
    // and show the same launch notice used by other unopened features.
    page = <PricingPage {...billingProps} />;
  } else if (path === "/payment/result") {
    // Payment result page reads the order query parameter and fetches order status.
    page = <PaymentResultPage {...billingProps} orderNo={search.get("order")} />;
  }

  const authBodyOverride = postAuthPath === "/canvas"
    ? (language === "en" ? "Sign in to open the canvas workspace." : "登录后即可进入功能画布。")
    : undefined;
  const canvasLoginRequested = path === "/canvas" || postAuthPath === "/canvas";

  return (
    <>
      {/* Site-wide visual background. */}
      {path === "/canvas" ? null : <GalaxyBackground />}
      {/* Current page body: homepage, account, pricing, checkout, mock payment, or result. */}
      {page}
      {/* Public user login/register modal. Successful auth stores the returned user in App state. */}
      <AuthModal
        open={authOpen}
        language={language}
        initialMode={initialAuthMode === "register" ? "register" : "login"}
        bodyOverride={authBodyOverride}
        canvasSession={canvasLoginRequested}
        onClose={() => {
          setAuthOpen(false);
          setPostAuthPath(null);
        }}
        onAuthenticated={(authenticatedUser, session) => {
          setUser(authenticatedUser);
          setAuthSession(session);
          setAuthOpen(false);
          setDownloadMessage(null);
          if (canvasLoginRequested) {
            sessionStorage.setItem(CANVAS_SESSION_STORAGE_KEY, authenticatedUser.id);
            setCanvasAuthenticated(true);
            setCanvasAuthChecked(true);
          }
          if (postAuthPath) {
            navigate(postAuthPath);
            setPostAuthPath(null);
          }
        }}
      />
      {/* Launch or development-mode notice modal. */}
      <LaunchNoticeModal open={launchNoticeOpen} onClose={() => setLaunchNoticeOpen(false)} />
    </>
  );
}
